import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import clsx from 'clsx';
import './Button.css';

/**
 * Variant — visual intent.
 *
 * - `primary`   green, main CTA ("Continue", "Start lesson")
 * - `secondary` terracotta, supporting action ("Listen again", "Skip")
 * - `danger`    red, destructive ("Delete account", "Leave lesson")
 * - `neutral`   white + border, cancel / less-urgent ("Cancel", "Later")
 * - `ghost`     transparent, tertiary ("Learn more", inline actions)
 */
export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'neutral' | 'ghost';

/**
 * Size — tuned for touch first. `md` is the default and always ≥52px tall.
 * `sm` is desktop-leaning (40px) — avoid for primary mobile CTAs.
 * `lg` for hero-level actions (60px).
 */
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Stretch to fill the horizontal container (common for mobile CTAs). */
  fullWidth?: boolean;
  /** Replaces label with a centered spinner; aria-busy announced to SR. */
  loading?: boolean;
  /** Button label (required). Keep it short. All-caps is applied visually. */
  children: ReactNode;
  /** Icon or element before the label. Accepts any ReactNode. */
  leadingIcon?: ReactNode;
  /** Icon or element after the label. */
  trailingIcon?: ReactNode;
  /** Ref forwarded to the underlying button element. */
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Button — the 3D-shadow primitive. See Button.css for physics.
 *
 * - Touch targets: sm 40px / md 52px / lg 60px.
 * - Tactile press: translateY(2px) + inset shadow shrinks on :active.
 * - Loading: spinner replaces label without layout shift; clicks suppressed.
 * - Disabled: 50% opacity, greyscale, no pointer events, no press animation.
 * - Focus-visible: brand-green focus ring (accent ring for secondary, red for danger).
 */
export function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  disabled,
  leadingIcon,
  trailingIcon,
  children,
  className,
  type,
  onClick,
  ref,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      disabled={disabled || undefined}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
      data-variant={variant}
      data-size={size}
      data-full-width={fullWidth || undefined}
      className={clsx('fluent-btn', className)}
      onClick={loading ? undefined : onClick}
      {...rest}
    >
      <span className="fluent-btn__label">
        {leadingIcon != null && <span className="fluent-btn__icon">{leadingIcon}</span>}
        <span>{children}</span>
        {trailingIcon != null && <span className="fluent-btn__icon">{trailingIcon}</span>}
      </span>
      {loading && (
        <span className="fluent-btn__spinner" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
            <path
              d="M22 12a10 10 0 0 1-10 10"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              fill="none"
            />
          </svg>
        </span>
      )}
    </button>
  );
}
